import React, { memo } from "react";
import { useImmer } from "use-immer";

import { ordersAdvancedGraphConfig } from "@intelligems/ig-graphs/graph-configs";
import { IgAnalyticsTooltip, IgToggleButtonGroup } from "@intelligems/ig-theme";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  GraphConfig,
  HistogramMetricType,
} from "@intelligems/ig-types";
import { ToggleButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useGetTinybirdExperienceAnalysis } from "@src/api/analytics/get-tinybird-experience-analysis";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useOrgTimezone } from "@src/api/organizations/get-organization";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { InsufficientCogsAlert } from "@src/features/analytics/baseDashboard/InsufficientCogsAlert";
import { findTooltip } from "@src/features/analytics/baseDashboard/utils";
import { VariationHeaderDropdown } from "@src/features/analytics/baseDashboard/VariationHeaderDropdown";
import { InteractiveChartLegend } from "@src/features/analytics/legends/InteractiveChartLegend";
import { MetricDetailSection } from "@src/features/analytics/metricDetails/MetricDetailSection";
import {
  chartTooltips,
  shouldShowInsufficientCogsWarning,
} from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { getControlVariationId } from "@src/utils/controlVariation";
import { getExperienceRunTimeNumeric } from "@src/utils/dateConverters";
import { UseQueryResult } from "@tanstack/react-query";

interface OrderAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const OrderAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: OrderAnalyticsPageProps) => {
    const timezone = useOrgTimezone();
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const dashboardFilters = useSelector(getExperienceAnalyticsFilters);

    const [histogramMetric, setHistogramMetric] = useImmer<HistogramMetricType>(
      "net_product_revenue"
    );
    const duration = getExperienceRunTimeNumeric({
      experienceStatus: experience.status,
      experienceActions: experience.experienceActions,
      timezone: timezone,
      dashboardEndTs: dashboardFilters.endTime as number | null | undefined,
      dashboardStartTs: dashboardFilters.startTime as number | null | undefined,
    });
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");
    const [selectedVariationId, setSelectedVariationId] = useImmer(
      dashboardFilters?.compareGroupB || getControlVariationId(variations)
    );

    const unitMixData = useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: [
          ordersAdvancedGraphConfig[0],
          ordersAdvancedGraphConfig[1],
        ],
        experience,
        overviewDataLoaded: data[0].isSuccess,
      },
      "experiences"
    );

    const unitQuantityHistogram = useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: [ordersAdvancedGraphConfig[2]],
        experience,
        overviewDataLoaded: data[0].isSuccess,
        histogramValues: {
          histogramMetric: "unit_quantity",
          kind: "histogram",
        },
      },
      "experiences"
    );

    const orderNetRevenueKde = useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: [ordersAdvancedGraphConfig[3]],
        experience,
        histogramValues: {
          histogramMetric,
          kind: "kde_cdf",
        },
        overviewDataLoaded: data[0].isSuccess,
      },
      "experiences"
    );

    const orderTimeSeries = useGetTinybirdExperienceAnalysis(
      {
        graphConfigs: [ordersAdvancedGraphConfig[5]],
        experience,
        interval: duration < 5 ? "HOUR" : duration < 21 ? "DAY" : "WEEK",
        overviewDataLoaded: data[0].isSuccess,
      },
      "experiences"
    );

    const [focusGroups, setFocusGroups] = useImmer<string[]>([]);

    const handleLegendClick = (dataKey: string) => {
      if (focusGroups.includes(dataKey)) {
        setFocusGroups(focusGroups.filter((el) => el !== dataKey));
      } else {
        setFocusGroups((prev) => [...prev, dataKey]);
      }
    };

    const configWithDuration = {
      ...ordersAdvancedGraphConfig[5],
      duration: duration,
    };

    return (
      <Stack direction="column" spacing={4}>
        {shouldShowInsufficientCogsWarning(data) && <InsufficientCogsAlert />}
        <Grid size={12}>
          <BaseMetricSection
            groupTitle={"Orders Over Time"}
            tooltip={"Number of orders placed on the site, by day."}
          >
            <GraphsWrapper
              testDuration={testDuration}
              totalTestVisitors={totalExperienceVisitors}
              hideHeader={true}
              config={ordersAdvancedGraphConfig[5]}
              isDataLoading={
                orderTimeSeries[0].isLoading || orderTimeSeries[0].isPending
              }
              renderLocation={"experiment"}
              data={selectData({
                dashboardData: orderTimeSeries,
                config: configWithDuration,
                _resource: experience,
                locale: storeCurrencyConfig?.value?.locale,
                metricFilterIndex: 0,
                variations,
                interval:
                  duration < 5 ? "HOUR" : duration < 21 ? "DAY" : "WEEK",
              })}
              compareVariationId={compareVariationId}
              rawData={data}
              intervalOfTheData={duration}
            />
          </BaseMetricSection>
        </Grid>
        <Grid size={12}>
          <BaseMetricSection
            // tooltip={'Very cool tooltip'}
            rightSideButtons={[
              <IgAnalyticsTooltip key={"filter-buttons"} title={""}>
                <IgToggleButtonGroup
                  disabled={orderNetRevenueKde[0].isLoading}
                  buttons={[
                    <ToggleButton
                      value="net_product_revenue"
                      key="net_product_revenue"
                      size={"small"}
                      sx={{
                        borderBottomLeftRadius: 9999,
                        borderTopLeftRadius: 9999,
                        paddingLeft: "12px",
                      }}
                    >
                      Net Product Revenue
                    </ToggleButton>,
                    <ToggleButton
                      value="net_revenue"
                      key="net_revenue"
                      size={"small"}
                      sx={{
                        borderBottomRightRadius: 9999,
                        borderTopRightRadius: 9999,
                        paddingRight: "12px",
                      }}
                    >
                      Net Revenue
                    </ToggleButton>,
                  ]}
                  value={histogramMetric}
                  control={{
                    exclusive: true,
                    onChange: (event, value: string) => {
                      if (value) {
                        setHistogramMetric(value as HistogramMetricType);
                      }
                    },
                  }}
                />
              </IgAnalyticsTooltip>,
            ]}
            groupTitle={"Order Revenue Distribution"}
          >
            <Grid size={12}>
              <MetricDetailSection
                title={ordersAdvancedGraphConfig[3].title[0]}
                metricDetails={{ isPrimary: false }}
                borderRadius={"6px"}
              >
                <BaseGraphPaper hideBorder={true}>
                  <GraphsWrapper
                    syncId={"123"}
                    testDuration={testDuration}
                    hideHeader={true}
                    focusGroups={focusGroups}
                    isDataLoading={
                      orderNetRevenueKde[0].isLoading ||
                      orderNetRevenueKde[0].isPending
                    }
                    focusedVariationId={selectedVariationId}
                    config={ordersAdvancedGraphConfig[3]}
                    totalTestVisitors={totalExperienceVisitors}
                    renderLocation={"experiment"}
                    data={selectData({
                      dashboardData: orderNetRevenueKde,
                      config: ordersAdvancedGraphConfig[3],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    compareVariationId={compareVariationId}
                    rawData={data}
                  />
                </BaseGraphPaper>
              </MetricDetailSection>
            </Grid>
            <Grid size={12}>
              <MetricDetailSection
                metricDetails={{ isPrimary: false }}
                title={ordersAdvancedGraphConfig[4].title[0]}
                borderRadius={"6px"}
              >
                <BaseGraphPaper hideBorder={true}>
                  <GraphsWrapper
                    syncId={"123"}
                    testDuration={testDuration}
                    hideHeader={true}
                    focusGroups={focusGroups}
                    config={ordersAdvancedGraphConfig[4]}
                    isDataLoading={
                      orderNetRevenueKde[0].isLoading ||
                      orderNetRevenueKde[0].isPending
                    }
                    totalTestVisitors={totalExperienceVisitors}
                    renderLocation={"experiment"}
                    data={selectData({
                      dashboardData: orderNetRevenueKde,
                      config: ordersAdvancedGraphConfig[4],
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    compareVariationId={compareVariationId}
                    rawData={data}
                  />
                </BaseGraphPaper>
              </MetricDetailSection>
            </Grid>
            <Grid size={12}>
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={focusGroups}
                lines={variations.map((variation) => {
                  return { id: variation.id, name: variation.name };
                })}
              />
            </Grid>
          </BaseMetricSection>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={6}>
            <BaseMetricSection
              groupTitle={ordersAdvancedGraphConfig[0].title[0]}
              tooltip={
                findTooltip(
                  ordersAdvancedGraphConfig[0],
                  chartTooltips,
                  "experiment",
                  0
                ) || undefined
              }
            >
              <GraphsWrapper
                testDuration={testDuration}
                totalTestVisitors={totalExperienceVisitors}
                hideHeader={true}
                isDataLoading={
                  unitMixData[0].isLoading || unitMixData[0].isPending
                }
                config={ordersAdvancedGraphConfig[0]}
                renderLocation={"experiment"}
                data={selectData({
                  dashboardData: unitMixData,
                  config: ordersAdvancedGraphConfig[0],
                  _resource: experience,
                  locale: storeCurrencyConfig?.value?.locale,
                  metricFilterIndex: 0,
                  variations,
                })}
                compareVariationId={compareVariationId}
                rawData={data}
                showWatermark={false}
              />
            </BaseMetricSection>
          </Grid>
          <Grid size={6}>
            <BaseMetricSection
              groupTitle={ordersAdvancedGraphConfig[1].title[0]}
              tooltip={
                findTooltip(
                  ordersAdvancedGraphConfig[1],
                  chartTooltips,
                  "experiment",
                  0
                ) || undefined
              }
            >
              <GraphsWrapper
                testDuration={testDuration}
                hideHeader={true}
                totalTestVisitors={totalExperienceVisitors}
                config={ordersAdvancedGraphConfig[1]}
                renderLocation={"experiment"}
                isDataLoading={
                  unitMixData[0].isLoading || unitMixData[0].isPending
                }
                data={selectData({
                  dashboardData: unitMixData,
                  config: ordersAdvancedGraphConfig[1],
                  _resource: experience,
                  locale: storeCurrencyConfig?.value?.locale,
                  metricFilterIndex: 0,
                  variations,
                })}
                compareVariationId={compareVariationId}
                rawData={data}
                showWatermark={false}
              />
            </BaseMetricSection>
          </Grid>
        </Grid>
        <Grid size={12}>
          <BaseMetricSection
            tooltip={
              "Distribution of orders by the quantity of units contained in each order."
            }
            headerDropdown={
              <VariationHeaderDropdown
                selectedVariationId={selectedVariationId}
                setSelectedVariationId={setSelectedVariationId}
              />
            }
            groupTitle={"Unit Quantity Histogram"}
          >
            <GraphsWrapper
              testDuration={testDuration}
              focusedVariationId={selectedVariationId}
              config={ordersAdvancedGraphConfig[2]}
              totalTestVisitors={totalExperienceVisitors}
              renderLocation={"experiment"}
              isDataLoading={
                unitQuantityHistogram[0].isLoading ||
                unitQuantityHistogram[0].isPending
              }
              data={selectData({
                dashboardData: unitQuantityHistogram,
                config: ordersAdvancedGraphConfig[2],
                _resource: experience,
                locale: storeCurrencyConfig?.value?.locale,
                metricFilterIndex: 0,
                variations,
              })}
              compareVariationId={compareVariationId}
              rawData={data}
            />
          </BaseMetricSection>
        </Grid>
      </Stack>
    );
  }
);
